import { ArrowDown, ArrowLeftRight, ArrowUp, Repeat } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router';
import { HeroTotal } from '../components/HeroTotal';
import { MonthCard } from '../components/MonthCard';
import { ReportsHomeCard } from '../components/ReportsHomeCard';
import { TransactionRow } from '../components/TransactionRow';
import { buttonVariants } from '../components/ui/button';
import { DebtsSummaryCard, DueSoonCard, NetWorthCard } from '../components/HomeCards';
import {
  BudgetHomeCard,
  ChildrenLimitsCard,
  GoalsStrip,
  MyLimitCard,
} from '../components/PlanningCards';
import { Card, ListGroup, SectionLabel } from '../components/ui/card';
import { useMe } from '../lib/auth';
import { todayIn } from '../lib/dates';
import { useBalanceSummary, useTransactions } from '../lib/ledger';

/** docs/DESIGN.md quick actions. */
const QUICK = [
  { kind: 'expense', Icon: ArrowDown, tone: 'text-expense' },
  { kind: 'income', Icon: ArrowUp, tone: 'text-accent' },
  { kind: 'transfer', Icon: ArrowLeftRight, tone: 'text-fg' },
  { kind: 'exchange', Icon: Repeat, tone: 'text-fg' },
] as const;

export function HomePage() {
  const { t } = useTranslation();
  const me = useMe();
  const location = useLocation();
  const summary = useBalanceSummary();
  const recent = useTransactions(5);
  const saved = (location.state as { saved?: boolean } | null)?.saved === true;
  if (!me.data) return <p role="status">{t('app.loading')}</p>;
  const items = recent.data?.pages[0]?.items ?? [];
  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="text-[22px] font-semibold">
          {t('home.greeting', { name: me.data.member.displayName })}
        </h1>
        <p className="text-muted">
          {t('home.familyLine', {
            family: me.data.family.name,
            role: t(`roles.${me.data.member.role}`),
          })}
        </p>
      </div>
      {saved && (
        <p
          role="status"
          className="rounded-[20px] border border-line bg-surface p-3 ps-4 text-[13px] font-medium"
        >
          {t('home.saved')}
        </p>
      )}
      {summary.data && <HeroTotal summary={summary.data} label={t('home.totalTitle')} />}
      <ul className="grid grid-cols-4 gap-2">
        {QUICK.map(({ kind, Icon, tone }) => (
          <li key={kind}>
            <Link
              to={`/add?kind=${kind}`}
              className="flex flex-col items-center gap-1.5 rounded-2xl text-xs font-medium focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
            >
              <span className="flex size-[52px] items-center justify-center rounded-[18px] bg-surface-2">
                <Icon aria-hidden="true" className={`size-[22px] ${tone}`} strokeWidth={2} />
              </span>
              {t(`add.kinds.${kind}`)}
            </Link>
          </li>
        ))}
      </ul>
      <MyLimitCard memberId={me.data.member.id} />
      <DueSoonCard today={todayIn(me.data.family.timezone)} />
      {summary.data && (
        <Link
          to="/reports"
          aria-label={t('home.openReports')}
          className="block rounded-3xl focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          <MonthCard summary={summary.data} />
        </Link>
      )}
      <ReportsHomeCard />
      <BudgetHomeCard />
      <ChildrenLimitsCard />
      <GoalsStrip />
      <DebtsSummaryCard />
      {summary.data && <NetWorthCard summary={summary.data} />}
      <section className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-3">
          <SectionLabel>{t('home.recent')}</SectionLabel>
          {items.length > 0 && (
            <Link
              to="/activity"
              className="inline-flex min-h-11 items-center rounded-xl px-1 text-[13px] font-semibold text-accent focus-visible:outline-2 focus-visible:outline-accent"
            >
              {t('home.seeAll')}
            </Link>
          )}
        </div>
        {recent.isPending ? (
          <p role="status" className="px-1 text-muted">
            {t('app.loading')}
          </p>
        ) : items.length === 0 ? (
          <Card className="flex flex-col gap-4">
            <p className="text-muted">{t('home.emptyRecent')}</p>
            <Link to="/add" className={buttonVariants()}>
              {t('add.title')}
            </Link>
          </Card>
        ) : (
          <ListGroup>
            {items.map((tx) => (
              <li key={tx.id}>
                <TransactionRow tx={tx} baseCurrency={me.data.family.baseCurrency} />
              </li>
            ))}
          </ListGroup>
        )}
      </section>
    </div>
  );
}
